import React from 'react';
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, subWeeks, subMonths, startOfYear, endOfYear, subYears } from 'date-fns';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import type { TimesheetReportFilters } from '@/types/timesheetReport';
import { Calendar as CalendarIcon, X } from 'lucide-react';
import { cn } from '@/lib/utils';

interface TimePeriodFiltersProps {
  filters: TimesheetReportFilters;
  onFiltersChange: (filters: TimesheetReportFilters) => void;
}

export const TimePeriodFilters: React.FC<TimePeriodFiltersProps> = ({
  filters,
  onFiltersChange,
}) => {
  // Quick preset buttons
  const presets = [
    {
      label: 'Today',
      getValue: () => {
        const now = new Date();
        return { from: now, to: now };
      },
    },
    {
      label: 'This Week',
      getValue: () => {
        const now = new Date();
        return {
          from: startOfWeek(now, { weekStartsOn: 1 }),
          to: endOfWeek(now, { weekStartsOn: 1 }),
        };
      },
    },
    {
      label: 'Last Week',
      getValue: () => {
        const lastWeek = subWeeks(new Date(), 1);
        return {
          from: startOfWeek(lastWeek, { weekStartsOn: 1 }),
          to: endOfWeek(lastWeek, { weekStartsOn: 1 }),
        };
      },
    },
    {
      label: 'This Month',
      getValue: () => {
        const now = new Date();
        return {
          from: startOfMonth(now),
          to: endOfMonth(now),
        };
      },
    },
    {
      label: 'Last Month',
      getValue: () => {
        const lastMonth = subMonths(new Date(), 1);
        return {
          from: startOfMonth(lastMonth),
          to: endOfMonth(lastMonth),
        };
      },
    },
    {
      label: 'This Quarter',
      getValue: () => {
        const now = new Date();
        const quarter = Math.floor(now.getMonth() / 3);
        const from = new Date(now.getFullYear(), quarter * 3, 1);
        const to = new Date(now.getFullYear(), quarter * 3 + 3, 0);
        return { from, to };
      },
    },
    {
      label: 'Last Quarter',
      getValue: () => {
        const now = new Date();
        const quarter = Math.floor(now.getMonth() / 3);
        const lastQuarter = quarter === 0 ? 3 : quarter - 1;
        const year = quarter === 0 ? now.getFullYear() - 1 : now.getFullYear();
        const from = new Date(year, lastQuarter * 3, 1);
        const to = new Date(year, lastQuarter * 3 + 3, 0);
        return { from, to };
      },
    },
    {
      label: 'This Year',
      getValue: () => {
        const now = new Date();
        return {
          from: startOfYear(now),
          to: endOfYear(now),
        };
      },
    },
    {
      label: 'Last Year',
      getValue: () => {
        const lastYear = subYears(new Date(), 1);
        return {
          from: startOfYear(lastYear),
          to: endOfYear(lastYear),
        };
      },
    },
  ];

  const handlePresetClick = (preset: typeof presets[0]) => {
    const { from, to } = preset.getValue();
    onFiltersChange({
      ...filters,
      dateFrom: from,
      dateTo: to,
    });
  };

  const handleClearDates = () => {
    onFiltersChange({
      ...filters,
      dateFrom: undefined,
      dateTo: undefined,
    });
  };

  const hasDateFilter = filters.dateFrom || filters.dateTo;

  return (
    <div className="space-y-6">
      {/* Quick Presets */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Quick Date Ranges</CardTitle>
          <CardDescription>
            Select a predefined date range for quick filtering
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-2">
            {presets.map((preset) => (
              <Button
                key={preset.label}
                variant="outline"
                size="sm"
                onClick={() => handlePresetClick(preset)}
                className="justify-start"
              >
                {preset.label}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Custom Date Range */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg">Custom Date Range</CardTitle>
              <CardDescription>
                Select specific start and end dates
              </CardDescription>
            </div>
            {hasDateFilter && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClearDates}
                className="gap-2 text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
                Clear
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-6">
            {/* From Date */}
            <div className="space-y-2">
              <Label>From Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      'w-full justify-start text-left font-normal',
                      !filters.dateFrom && 'text-muted-foreground'
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {filters.dateFrom ? (
                      format(filters.dateFrom, 'PPP')
                    ) : (
                      <span>Pick a date</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={filters.dateFrom}
                    onSelect={(date) =>
                      onFiltersChange({
                        ...filters,
                        dateFrom: date,
                      })
                    }
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* To Date */}
            <div className="space-y-2">
              <Label>To Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      'w-full justify-start text-left font-normal',
                      !filters.dateTo && 'text-muted-foreground'
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {filters.dateTo ? (
                      format(filters.dateTo, 'PPP')
                    ) : (
                      <span>Pick a date</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={filters.dateTo}
                    onSelect={(date) =>
                      onFiltersChange({
                        ...filters,
                        dateTo: date,
                      })
                    }
                    initialFocus
                    disabled={(date) =>
                      filters.dateFrom ? date < filters.dateFrom : false
                    }
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          {/* Current Selection Display */}
          {hasDateFilter && (
            <div className="mt-4 p-3 bg-muted rounded-md">
              <p className="text-sm font-medium mb-1">Selected Range:</p>
              <p className="text-sm text-muted-foreground">
                {filters.dateFrom ? format(filters.dateFrom, 'MMM d, yyyy') : 'Start'} →{' '}
                {filters.dateTo ? format(filters.dateTo, 'MMM d, yyyy') : 'End'}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Help Text */}
      <div className="text-sm text-muted-foreground bg-blue-50 dark:bg-blue-950/20 p-4 rounded-md border border-blue-200 dark:border-blue-900">
        <p className="font-medium text-blue-900 dark:text-blue-100 mb-1">💡 Tip</p>
        <p className="text-blue-800 dark:text-blue-200">
          Date filters apply to the "Week Ending" date of timesheets. Use quick presets for common
          ranges or select custom dates for specific periods.
        </p>
      </div>
    </div>
  );
};
